import React from 'react';
import {
    useSelector,
} from 'react-redux';

import PropTypes from 'prop-types';

import format from 'date-fns/format';
import isToday from 'date-fns/is_today';
import isYesterday from 'date-fns/is_yesterday';
import isThisYear from 'date-fns/is_this_year';

import {
    Col,
    Row,
} from '@zillow/zrs-comet';

import {
    Text,
    TextButton,
} from '@zillow/constellation';

// Selectors
import {
    RoutingSelectors,
} from 'app/shared/selectors';

import { RMWContext } from 'app/shared/providers/RMWContext';
import { getTagTypeByKey, STATUS_KEY } from 'app/shared/utils/statusTagUtils';

import {
    StyledCard,
    StyledIconChevronRightCircle,
    StyledParagraphForNameDisplay,
    StyledSubTextContainer,
    StyledTag,
} from './StyledComponents';


const APPLICATION_STATUS_TYPES = {
    INVITED: 'invited',
    PENDING: 'pending',
    COMPLETED: 'completed',
    ACCEPTED: 'accepted',
    WITHDRAWN: 'withdrawn',
};

const APPLICATION_STATUS_DISPLAY_TEXTS = {
    [APPLICATION_STATUS_TYPES.INVITED]: 'Invited To Apply',
    [APPLICATION_STATUS_TYPES.COMPLETED]: 'Received',
    [APPLICATION_STATUS_TYPES.PENDING]: 'In-Progress',
};

const getStatusDisplayTextForApplicationStatus = (status) => {
    switch (status) {
        case APPLICATION_STATUS_TYPES.INVITED:
            return APPLICATION_STATUS_DISPLAY_TEXTS[APPLICATION_STATUS_TYPES.INVITED];
        case APPLICATION_STATUS_TYPES.COMPLETED:
            return APPLICATION_STATUS_DISPLAY_TEXTS[APPLICATION_STATUS_TYPES.COMPLETED];
        case APPLICATION_STATUS_TYPES.PENDING:
            return APPLICATION_STATUS_DISPLAY_TEXTS[APPLICATION_STATUS_TYPES.PENDING];
        default:
            return status;
    }
};

const getApplicantsDisplayText = (applicants = []) => {
    if (!applicants.length) {
        return '';
    }

    let displayText = '';
    let additionalApplicantsCount = 0;

    applicants.forEach((applicant, idx) => {
        // If first item
        if (!idx) {
            displayText = applicant.name;
        } else {
            additionalApplicantsCount += 1;
        }
    });

    if (additionalApplicantsCount) {
        displayText += ` + ${additionalApplicantsCount}`;
    }

    return displayText;
};

const getNumTotalCompletedApplicants = (applicants = []) => {
    if (!applicants.length) {
        return 0;
    }

    let numTotalCompletedApplicants = 0;

    applicants.forEach((applicant) => {
        if (applicant.status === 'completed') {
            numTotalCompletedApplicants += 1;
        }
    });

    return numTotalCompletedApplicants;
};

const getTimestamp = (updateDateUtc) => {
    const { year, month, day } = updateDateUtc.date;
    const { hour, minute, second } = updateDateUtc.time;
    const dateInLocalTZ = new Date(`${month}/${day}/${year} ${hour}:${minute}:${second} UTC`);
    const isTimestampToday = isToday(dateInLocalTZ);
    const isTimestampYesterday = isYesterday(dateInLocalTZ);

    let timestamp = format(dateInLocalTZ, 'dddd, MMM D');

    if (!isThisYear(dateInLocalTZ)) {
        timestamp = format(dateInLocalTZ, 'MMM D, YYYY');
    } else if (isTimestampToday || isTimestampYesterday) {
        const formattedTime = format(dateInLocalTZ, 'h:mm A');
        const dayText = isTimestampToday ? 'Today' : 'Yesterday';

        timestamp = `${dayText} at ${formattedTime}`;
    }

    return timestamp;
};

const ApplicationItem = ({
    status,
    applicants,
    applicationIsViewed,
    updateDateUtc,
    conversationId,
    applicationId,
    onApplicationItemClick,
}) => {
    const { paths } = React.useContext(RMWContext);
    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    const applicantsDisplayName = getApplicantsDisplayText(applicants);
    const connectedStatusType = getTagTypeByKey(STATUS_KEY.INTERESTED_RENTERS, status);
    const isApplicationReceived = status === APPLICATION_STATUS_TYPES.COMPLETED;
    const statusDisplayText = getStatusDisplayTextForApplicationStatus(status);
    const showNewBadge = isApplicationReceived && !applicationIsViewed;
    const numTotalCompletedApplicants = getNumTotalCompletedApplicants(applicants);

    const SubText = () => {
        if (status === APPLICATION_STATUS_TYPES.PENDING) {
            return (
                <StyledSubTextContainer>
                    <Text
                        paddingTop="xs"
                        fontType="bodySmall"
                    >
                        {`${numTotalCompletedApplicants} of ${applicants.length} have applied`}
                    </Text>
                </StyledSubTextContainer>
            );
        }

        const timestamp = getTimestamp(updateDateUtc);
        return (
            <StyledSubTextContainer>
                {
                    showNewBadge && (
                        <>
                            <Text fontType="bodySmall" fontColor="orange400">New</Text>
                            {' '}
                            &#183;
                            {' '}
                        </>
                    )
                }
                <Text fontType="bodySmall">{timestamp}</Text>
            </StyledSubTextContainer>
        );
    };

    const CtaLink = () => {
        let ctaText = 'View';

        if (status === APPLICATION_STATUS_TYPES.INVITED || status === APPLICATION_STATUS_TYPES.WITHDRAWN) {
            ctaText = 'Message';
        }

        return (
            <TextButton fontType="bodySmallHeading">
                {ctaText}
                <StyledIconChevronRightCircle size="sm" />
            </TextButton>
        );
    };

    const handleCardClick = () => {
        let linkUrl = `${paths.base}/${paths.propertyDetailsApplications}/${applicationId}?propertyId=${listingId}`;

        if (status === APPLICATION_STATUS_TYPES.INVITED || status === APPLICATION_STATUS_TYPES.WITHDRAWN) {
            linkUrl = `${paths.base}/inbox/${listingId}/${conversationId}`;
        }

        onApplicationItemClick(status);
        window.router.updateLocation(linkUrl);
    };

    return (
        <StyledCard cardType="secondary" onClick={handleCardClick}>
            <StyledTag size="sm" appearance={connectedStatusType}>{statusDisplayText}</StyledTag>
            <Row>
                <Col>
                    <StyledParagraphForNameDisplay paddingTop="xs" fontType="bodySmallHeading">
                        {applicantsDisplayName}
                    </StyledParagraphForNameDisplay>

                </Col>
            </Row>
            <Row>
                <Col>
                    <SubText />
                </Col>
            </Row>
            <CtaLink />
        </StyledCard>
    );
};

ApplicationItem.propTypes = {
    status: PropTypes.string.isRequired,
    applicants: PropTypes.array.isRequired,
    applicationIsViewed: PropTypes.bool.isRequired,
    updateDateUtc: PropTypes.object.isRequired,
    conversationId: PropTypes.string.isRequired,
    applicationId: PropTypes.string.isRequired,
    onApplicationItemClick: PropTypes.func,
};

ApplicationItem.defaultProps = {
    onApplicationItemClick: () => {},
};

export default ApplicationItem;
